import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import { GET_SESSION, GET_CURRENT_USER } from "@/graphql/queries";
import { REPORT_SESSION_ISSUE } from "@/graphql/mutations";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Clock, Users, Coins, Calendar, AlertCircle, Copy, Check, User, Radio } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ApiErrorScreen, useHasCriticalError } from "@/components/ApiErrorScreen";

interface SessionParticipant {
  id: string;
  participantName?: string;
  participantIdentity?: string;
  isModerator: boolean;
  joinedAt: string;
  leftAt?: string;
}

interface StreamingPeriod {
  id: string;
  ingressId: string;
  startedAt: string;
  endedAt?: string;
}

interface Session {
  id: string;
  date: string;
  duration: string;
  participants: number;
  creditsUsed: number;
  status: "COMPLETED" | "ACTIVE" | "CANCELLED";
  createdAt: string;
  flowRoomName?: string;
  title?: string;
  description?: string;
  notes?: string;
  createdBy?: {
    id: string;
    name?: string;
    email: string;
  };
  sessionParticipants?: SessionParticipant[];
  streamingPeriods?: StreamingPeriod[];
}

const SessionDetail = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isCopied, setIsCopied] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [issueDescription, setIssueDescription] = useState("");

  const { data, loading, error } = useQuery(GET_SESSION, {
    variables: { id: sessionId },
    skip: !sessionId,
    errorPolicy: 'all',
  });

  // Get current user for participant identity
  const { data: userData, error: userError } = useQuery<{ me?: { id: string; email: string } | null }>(GET_CURRENT_USER, { errorPolicy: 'all' });

  // Check for critical errors
  const queryErrors = [error, userError];
  const hasCriticalErrors = useHasCriticalError(queryErrors);
  const currentUser = userData?.me;
  const participantIdentity = currentUser?.email || currentUser?.id || "unknown";

  const [reportIssue, { loading: reportingIssue }] = useMutation(REPORT_SESSION_ISSUE, {
    onCompleted: () => {
      toast({
        title: "Issue reported",
        description: "Thank you for reporting the issue. We'll look into it.",
      });
      setIsReportDialogOpen(false);
      setIssueDescription("");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to report issue. Please try again.",
        variant: "destructive",
      });
    },
  });

  const session = (data as { session?: Session })?.session as Session;

  const handleCopyNotes = async () => {
    if (!session?.notes) return;

    try {
      await navigator.clipboard.writeText(session.notes);
      setIsCopied(true);
      toast({
        title: "Copied!",
        description: "Notes copied to clipboard.",
      });
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy notes to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleReportIssue = async () => {
    if (!sessionId || !issueDescription.trim()) return;

    await reportIssue({
      variables: {
        input: {
          sessionId,
          participantIdentity,
          issueDescription: issueDescription.trim(),
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 max-w-5xl">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading session details...</p>
        </div>
      </div>
    );
  }

  // Show blocking error screen when critical APIs fail
  if (hasCriticalErrors) {
    return (
      <ApiErrorScreen 
        errors={queryErrors}
        context="Dashboard - Session Details"
      />
    );
  }

  if (!session) {
    return (
      <div className="container mx-auto px-6 py-8 max-w-5xl">
        <div className="text-center py-12 space-y-4">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
          <p className="text-destructive">Session not found</p>
          <Button onClick={() => navigate("/history")} variant="outline">
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to History
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-5xl">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/history")}
        className="mb-6"
      >
        <ArrowLeft className="mr-2 w-4 h-4" />
        Back to History
      </Button>

      <div className="space-y-6">
        {/* Session Header */}
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2 flex-1">
              <h1 className="text-3xl font-bold">
                {session.title || "Session Details"}
              </h1>
              {session.title && (
                <p className="text-muted-foreground">{session.date}</p>
              )}
            </div>
            <Badge
              variant={session.status === "COMPLETED" ? "default" : "secondary"}
              className="text-sm px-3 py-1"
            >
              {session.status.toLowerCase()}
            </Badge>
          </div>
        </div>

        {/* Overview Section */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Overview</h2>
          <div className="space-y-4">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>Date</span>
                </div>
                <p className="font-medium">{session.date}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Duration</span>
                </div>
                <p className="font-medium">{session.duration}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Users className="w-4 h-4" />
                  <span>Participants</span>
                </div>
                <p className="font-medium">{session.participants}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Coins className="w-4 h-4 text-primary" />
                  <span>Credits Used</span>
                </div>
                <p className="font-medium text-primary">{session.creditsUsed}</p>
              </div>
              {session.createdBy && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <User className="w-4 h-4" />
                    <span>Created By</span>
                  </div>
                  <p className="font-medium">{session.createdBy.name || session.createdBy.email}</p>
                </div>
              )}
            </div>

            {/* Description */}
            {session.description && (
              <div className="pt-4 border-t border-border">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Description
                </h3>
                <p className="text-foreground">{session.description}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Participants Section */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Participants</h2>
          {(() => {
            const participants = session?.sessionParticipants || [];
            return participants.length > 0 ? (
              <div className="space-y-3">
                {participants.map((participant) => (
                  <div
                    key={participant.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">
                            {participant.participantName || participant.participantIdentity || 'Unknown'}
                          </p>
                          {participant.isModerator && (
                            <Badge variant="secondary" className="text-xs">
                              Moderator
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-0.5">
                          <div>
                            <span className="font-medium text-foreground/70">Joined:</span>{" "}
                            <span>{new Date(participant.joinedAt).toLocaleString()}</span>
                          </div>
                          {participant.leftAt && (
                            <div>
                              <span className="font-medium text-foreground/70">Left:</span>{" "}
                              <span>{new Date(participant.leftAt).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No participants recorded for this session.
              </p>
            );
          })()}
        </Card>

        {/* Flow Stream Section */}
        {(() => {
          const streamingPeriods = session?.streamingPeriods || [];
          return streamingPeriods.length > 0 ? (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Radio className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Flow Stream</h2>
              </div>
              <div className="space-y-3">
                {streamingPeriods.map((period) => (
                  <div
                    key={period.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-primary/5 border border-primary/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Radio className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">Stream Source</p>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-0.5">
                          <div>
                            <span className="font-medium text-foreground/70">Started:</span>{" "}
                            <span>{new Date(period.startedAt).toLocaleString()}</span>
                          </div>
                          {period.endedAt && (
                            <div>
                              <span className="font-medium text-foreground/70">Ended:</span>{" "}
                              <span>{new Date(period.endedAt).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : null;
        })()}

        {/* Notes Section */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Notes</h2>
            {session?.notes && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyNotes}
                disabled={isCopied}
              >
                {isCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            )}
          </div>

          <div className="text-sm leading-relaxed whitespace-pre-wrap">
            {session?.notes ? (
              <p>{session.notes}</p>
            ) : (
              <p className="text-muted-foreground italic">
                No notes for this session.
              </p>
            )}
          </div>
        </Card>

        {/* Recordings Section - Coming Soon */}
        <Card className="p-6 bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-2">Recordings</h2>
              <p className="text-sm text-muted-foreground">
                Session recordings will be available here
              </p>
            </div>
            <Badge variant="secondary">Coming Soon</Badge>
          </div>
        </Card>

        {/* Transcripts Section - Coming Soon */}
        <Card className="p-6 bg-muted/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold mb-2">Transcripts</h2>
              <p className="text-sm text-muted-foreground">
                Session transcripts will be available here
              </p>
            </div>
            <Badge variant="secondary">Coming Soon</Badge>
          </div>
        </Card>

        {/* Report Issue Section */}
        <Card className="p-6 border-destructive/20">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Report an Issue</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Found a problem with this session? Let us know and we'll investigate.
              </p>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsReportDialogOpen(true)}
              >
                Report Issue
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Report Issue Dialog */}
      <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Report Session Issue</DialogTitle>
            <DialogDescription>
              Please describe the issue you encountered during this session.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              placeholder="Describe the issue..."
              className="min-h-[120px]"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsReportDialogOpen(false);
                setIssueDescription("");
              }}
              disabled={reportingIssue}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReportIssue}
              disabled={reportingIssue || !issueDescription.trim()}
            >
              {reportingIssue ? "Reporting..." : "Submit Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SessionDetail;

